import { LoadRangeSlider } from './RangeSliderEngine';
import { LoadTextEditor } from './TextEditorEngine';
import { ElementClassManager } from './ElementClassManager';
import { LoadChipOverflowObserver } from './ChipEngine';
import { LoadHorizontalScrollObserver } from "./ScrollEngine";

namespace SignalHub.Browser.Utils {
    export function Main() {
        (window as any).ItemsManager = {
            items: [],
            add: function (ref: any, visual: any) {
                this.items.push({ ref: ref, visual: visual });
            },
            get: function (ref: any) {
                let item = this.items.find((v: any) => v.ref == ref);
                return item && item.visual ? item.visual : null;
            },
            remove: function (ref: any) {
                let index = this.items.indexOf(this.items.find((v: any) => v.ref == ref))
                this.items.splice(index, 1);
            }
        };

        (window as any).Modal = {
            show: function (ref: any) {
                ref.modal('show');
            },
            hide: function (ref: any) {
                ref.modal('hide');
            }
        };

        (window as any).DownloadFile = {
            downloadFromUrl: function (url: string, fileName?: string): boolean {
                let error = false;
                try {
                    const anchorElement = document.createElement('a');
                    anchorElement.href = url;
                    anchorElement.target = "_blank";
                    anchorElement.download = fileName ?? '';
                    anchorElement.click();
                    anchorElement.remove();
                }
                catch (ex) {
                    error = true;
                }
                return error;
            },
            downloadFromByteArray: function (filename: string, contentType: string, content: any): boolean {
                let error = false;
                try {
                    // Create the URL
                    const file = new File([content], filename, { type: contentType });
                    const exportUrl = URL.createObjectURL(file);

                    // Create the <a> element and click on it
                    const a = document.createElement("a");
                    document.body.appendChild(a);
                    a.href = exportUrl;
                    a.download = filename;
                    a.target = "_self";
                    a.click();

                    // We don't need to keep the url, let's release the memory
                    // On Safari it seems you need to comment this line... (please let me know if you know why)
                    URL.revokeObjectURL(exportUrl);
                }
                catch (ex) {
                    error = true;
                }
                return error;
            },
            downloadViaFetch: async function (url: string, fileName: string): Promise<boolean> {
                let error = false;
                try {
                    const response = await fetch(url);
                    if (!response.ok) {
                        return true;
                    }
                    const blob = await response.blob();
                    const exportUrl = URL.createObjectURL(blob);

                    const a = document.createElement("a");
                    document.body.appendChild(a);
                    a.href = exportUrl;
                    a.download = fileName;
                    a.target = "_self";
                    a.click();

                    URL.revokeObjectURL(exportUrl);
                    a.remove();
                }
                catch (ex) {
                    error = true;
                }
                return error;
            },
        };

        (window as any).Locale = {
            getLanguage() {
                return navigator.language || (navigator.languages && navigator.languages.length && navigator.languages[0]) || 'en'
            },

            getShort(language: any) {
                if (language.includes('nb')) return 'nb';
                if (language.includes('no')) return 'nb';
                if (language.includes('sv')) return 'sv';
                if (language.includes('da')) return 'da';
                if (language.includes('fi')) return 'fi';
                if (language.includes('ru')) return 'ru';
                return "default";
            },

            getBigNumberPrefixes: function (customLanguage: any) {
                let language = customLanguage ?? this.getShort(this.getLanguage());
                switch (language) {
                    case 'nn':
                    case 'nb':
                    case 'no':
                        return { billion: 'mrd.', million: 'mill.', thousand: 'T' };
                    case 'sv':
                        return { billion: 'mdr', million: 'm', thousand: 'T' };
                    case 'da':
                        return { billion: 'mia.', million: 'mio.', thousand: 'T' };
                    case 'fi':
                        return { billion: 'mrd.', million: 'milj.', thousand: 'T' };
                    case 'ru':
                        return { billion: 'млрд', million: 'млн', thousand: 'тыс' };
                    default:
                        return { billion: 'B', million: 'M', thousand: 'T' };
                }
            }
        };

        (window as any).Clipboard = {
            copyText: async function (text: string): Promise<boolean> {
                try {
                    await navigator.clipboard.writeText(text);
                    return true;
                }
                catch (err) {
                    console.error('Failed to copy: ', err);
                    return false;
                }
            }
        };

        (window as any).Zoom = {
            moveDomElement: function (element: any, targetId: any) {
                const target = document.getElementById(targetId);
                if (element && target) {
                    target.appendChild(element);
                }
            }
        };

        (window as any).TextTruncation = {
            getHeights: function (element: HTMLElement): Promise<{ scrollHeight: number, clientHeight: number, parentCardHeight: number } | null> {
                return new Promise((resolve) => {
                    requestAnimationFrame(() => {
                        if (!element) {
                            resolve(null);
                            return;
                        }

                        const card = element.closest('.mud-card');
                        const cardContent = card?.querySelector('.mud-card-content.card-body');
                        const parentHeight = cardContent ? (cardContent as HTMLElement).clientHeight : 0;
                        resolve({
                            scrollHeight: element.scrollHeight,
                            clientHeight: element.clientHeight,
                            parentCardHeight: parentHeight
                        });
                    });
                });
            },
            isDesktop: function (): boolean {
                return window.innerWidth >= 960;
            },
            getParentCardHeight: function (element: HTMLElement): number | null {
                if (!element)
                    return null;

                const card = element.closest('.mud-card');
                if (!card)
                    return null;

                const cardContent = card.querySelector('.mud-card-content');
                if (!cardContent)
                    return null;

                return (cardContent as HTMLElement).clientHeight;
            }
        };

    }

    (window as any).ElementClassManager = new ElementClassManager();
}

namespace SignalHub.Browser.Controls.RangeSlider {
    export function Main() {
        LoadRangeSlider();
    }
}

namespace SignalHub.Browser.Controls.TextEditor {
    export function Main() {
        LoadTextEditor();
    }
}

namespace SignalHub.Browser.Controls.ChipOverflowObserver {
    export function Main() {
        LoadChipOverflowObserver();
    }
}

namespace SignalHub.Browser.Controls.ScrollObserver {
    export function Main() {
        LoadHorizontalScrollObserver();
    }
}

SignalHub.Browser.Utils.Main();
SignalHub.Browser.Controls.RangeSlider.Main();
SignalHub.Browser.Controls.TextEditor.Main();
SignalHub.Browser.Controls.ChipOverflowObserver.Main();
SignalHub.Browser.Controls.ScrollObserver.Main();
